import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Camera, Calculator, Info, CheckCircle2, Upload, Image as ImageIcon, Smartphone, MessageSquare } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function DamageWizard({ token, onResult, accent = "#111827", message, onMessageChange }) {
    const [cat, setCat] = useState(null);
    const [answers, setAnswers] = useState({});
    const [estimate, setEstimate] = useState(null);
    const [computing, setComputing] = useState(false);
    const [uploads, setUploads] = useState([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        axios.get(`${BASE}/api/public/leasing/${token}/damage-catalog`)
            .then((r) => { setCat(r.data); setUploads(r.data.uploads || []); if (r.data.existing) { setEstimate(r.data.existing); setAnswers(r.data.existing.answers || {}); } else { const km = r.data.km || {}; setAnswers((p) => ({ maintenance_done: "0", maintenance_km: "0", agreed_km: km.agreed_km ? String(km.agreed_km) : "", current_km: km.current_km ? String(km.current_km) : "", ...p })); } })
            .catch(() => {});
    }, [token]);

    useEffect(() => { if (estimate && onResult) onResult(estimate); /* eslint-disable-next-line */ }, [estimate]);

    const set = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

    const compute = async () => {
        setComputing(true);
        try {
            const { data } = await axios.post(`${BASE}/api/public/leasing/${token}/damage-estimate`, { answers });
            setEstimate(data.estimate);
        } catch { /* noop */ }
        finally { setComputing(false); }
    };

    const onUpload = async (e, itemKey) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("item_key", itemKey || "");
        try {
            const { data } = await axios.post(`${BASE}/api/public/leasing/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploads((u) => [...u, data.upload]);
        } catch { /* noop */ }
        finally { setUploading(false); e.target.value = ""; }
    };

    if (!cat) return <div className="bg-white border rounded-lg p-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

    return (
        <div className="space-y-5" data-testid="leasing-damage-wizard">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex gap-2" data-testid="leasing-damage-disclaimer">
                <Info className="w-4 h-4 mt-0.5 shrink-0" /> {cat.disclaimer}
            </div>

            {cat.groups.map((g) => (
                <div key={g.group} className="bg-white border rounded-lg p-4 shadow-sm" data-testid={`leasing-damage-group-${g.group}`}>
                    <div className="font-bold text-gray-800 mb-2">{g.group}</div>
                    <div className="space-y-2">
                        {g.items.map((it) => (
                            <div key={it.key} className="flex flex-wrap items-center gap-2 py-1 border-b border-gray-50 last:border-0">
                                {it.input === "auto" && (
                                    <div className="flex-1 flex justify-between text-sm text-gray-600"><span>{it.label}{it.key === "service" && <em className="text-gray-400 not-italic"> · fällt immer an</em>}</span><span className="font-semibold">{eur(it.price)}</span></div>
                                )}
                                {it.input === "bool" && it.exclusive_group === "pauschale" && (
                                    <label className="flex-1 flex items-center gap-2 text-sm cursor-pointer" data-testid={`leasing-damage-${it.key}`}>
                                        <input type="radio" name="excl-pauschale"
                                            checked={it.key === "gutachten" ? !!answers.gutachten : !answers.gutachten}
                                            onChange={() => setAnswers((p) => ({ ...p, service: it.key === "service", gutachten: it.key === "gutachten" }))} />
                                        <span className="flex-1">{it.label}</span>
                                        <span className="text-gray-900 font-bold tabular-nums">{eur(it.price)}</span>
                                    </label>
                                )}
                                {it.input === "bool" && !it.exclusive_group && (
                                    <label className="flex-1 flex items-center gap-2 text-sm cursor-pointer" data-testid={`leasing-damage-${it.key}`}>
                                        <input type="checkbox" checked={!!answers[it.key]} onChange={(e) => set(it.key, e.target.checked)} />
                                        <span className="flex-1">{it.label}</span>
                                        <span className="text-gray-900 font-bold tabular-nums">{eur(it.price)}</span>
                                    </label>
                                )}
                                {it.input === "manual" && (
                                    <div className="flex-1 flex items-center gap-2 text-sm">
                                        <span className="flex-1">{it.label}</span>
                                        <input type="number" min="0" placeholder="€" value={answers[it.key] || ""} onChange={(e) => set(it.key, e.target.value)} className="w-24 border rounded-sm px-2 py-1" data-testid={`leasing-damage-${it.key}`} />
                                    </div>
                                )}
                                {it.input === "select" && (
                                    <div className="flex-1 flex items-center gap-2 text-sm">
                                        <span className="flex-1">{it.label}</span>
                                        <select value={answers[it.key] || "none"} onChange={(e) => set(it.key, e.target.value)} className="border rounded-sm px-2 py-1 text-sm" data-testid={`leasing-damage-${it.key}`}>
                                            {it.options.map((o) => <option key={o.v} value={o.v}>{o.label}{o.price ? ` (${eur(o.price)})` : ""}</option>)}
                                        </select>
                                    </div>
                                )}
                                {it.input === "maintenance" && (
                                    <div className="flex-1 space-y-2 text-sm" data-testid="leasing-damage-maintenance">
                                        <div className="text-gray-600">{it.label}</div>
                                        <div className="flex flex-wrap gap-3 items-center">
                                            <label className="flex items-center gap-1">Durchgeführte Wartungen*:
                                                <input type="number" min="0" required value={answers.maintenance_done ?? "0"} onChange={(e) => set("maintenance_done", e.target.value)} className="w-16 border rounded-sm px-2 py-1" data-testid="leasing-damage-maint-done" />
                                            </label>
                                            <label className="flex items-center gap-1">km-Stand*:
                                                <input type="number" min="0" required value={answers.maintenance_km ?? "0"} onChange={(e) => set("maintenance_km", e.target.value)} className="w-24 border rounded-sm px-2 py-1" data-testid="leasing-damage-maint-km" />
                                            </label>
                                        </div>
                                    </div>
                                )}
                                {it.sample_url && (
                                    <a href={`${BASE}${it.sample_url}`} target="_blank" rel="noreferrer" className="text-[11px] text-gray-600 underline cursor-pointer inline-flex items-center gap-1" data-testid={`leasing-damage-sample-${it.key}`}>
                                        <ImageIcon className="w-3.5 h-3.5" /> Muster ansehen
                                    </a>
                                )}
                                {(it.input === "bool" || it.input === "select" || it.input === "manual") && (
                                    <label className="text-xs font-bold cursor-pointer inline-flex items-center gap-1.5 border-2 rounded-md px-2.5 py-1.5 hover:bg-black/5" style={{ color: accent, borderColor: accent }} data-testid={`leasing-damage-upload-${it.key}`}>
                                        <Camera className="w-5 h-5" /> Foto
                                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onUpload(e, it.key)} />
                                    </label>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {(() => {
                const km = cat.km || {};
                if (!km.enabled) return null;
                const agreed = parseFloat(answers.agreed_km || 0) || 0;
                const current = parseFloat(answers.current_km || 0) || 0;
                const free = Number(km.free_allowance || 0);
                let line = null;
                if (agreed > 0 && current > 0) {
                    const diff = current - agreed;
                    if (diff > free) {
                        const mehr = diff - free;
                        const amt = Math.round(mehr * Number(km.mehr_cent || 0) / 100 * 1.19 * 100) / 100;
                        line = { kind: "mehr", km: Math.round(mehr), amount: amt };
                    } else if (-diff > free) {
                        const minder = Math.min(-diff - free, Number(km.max_minder || 999999));
                        const amt = Math.round(minder * Number(km.minder_cent || 0) / 100 * 1.19 * 100) / 100;
                        line = { kind: "minder", km: Math.round(minder), amount: amt };
                    } else {
                        line = { kind: "ok", km: Math.round(Math.abs(diff)) };
                    }
                }
                return (
                    <div className="bg-white border rounded-lg p-4 shadow-sm" data-testid="leasing-km-block">
                        <div className="font-bold text-gray-800 mb-1 flex items-center gap-2"><Calculator className="w-4 h-4 text-gray-500" /> Mehr-/Minderkilometer</div>
                        <p className="text-[12px] text-gray-500 mb-3">Kilometer-Leasing: {free.toLocaleString("de-DE")} km Toleranz. Mehrkilometer werden nachberechnet, Minderkilometer bis {Number(km.max_minder || 0).toLocaleString("de-DE")} km vergütet.</p>
                        <div className="flex flex-wrap gap-4 items-center text-sm">
                            <label className="flex items-center gap-1">Vereinbarte km-Laufleistung:
                                <input type="number" min="0" value={answers.agreed_km || ""} onChange={(e) => set("agreed_km", e.target.value)} className="w-28 border rounded-sm px-2 py-1" data-testid="leasing-km-agreed" />
                            </label>
                            <label className="flex items-center gap-1">Aktueller km-Stand:
                                <input type="number" min="0" value={answers.current_km || ""} onChange={(e) => set("current_km", e.target.value)} className="w-28 border rounded-sm px-2 py-1" data-testid="leasing-km-current" />
                            </label>
                        </div>
                        {line && line.kind === "mehr" && (
                            <div className="mt-3 flex justify-between items-center rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm" data-testid="leasing-km-preview">
                                <span className="text-red-800 font-semibold">Mehrkilometer ({line.km.toLocaleString("de-DE")} km über Toleranz)</span>
                                <span className="font-black text-red-800">+ {eur(line.amount)}</span>
                            </div>
                        )}
                        {line && line.kind === "minder" && (
                            <div className="mt-3 flex justify-between items-center rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm" data-testid="leasing-km-preview">
                                <span className="text-green-800 font-semibold">Minderkilometer-Vergütung ({line.km.toLocaleString("de-DE")} km)</span>
                                <span className="font-black text-green-800">− {eur(line.amount)}</span>
                            </div>
                        )}
                        {line && line.kind === "ok" && (
                            <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600" data-testid="leasing-km-preview">Innerhalb der Toleranz — keine Nach-/Verrechnung.</div>
                        )}
                    </div>
                );
            })()}

            <div className="rounded-lg p-4 border-2" style={{ borderColor: accent, background: "#FFF7ED" }} data-testid="leasing-photo-guide-block">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-black" style={{ color: accent }}>
                            <Smartphone className="w-4 h-4" /> Foto-Guide fürs Handy
                        </div>
                        <h4 className="text-base font-black text-gray-900 mt-1">Fahrzeug-Fotos Schritt für Schritt</h4>
                        <p className="text-sm text-gray-600 mt-1">Wir führen Sie durch alle nötigen Aufnahmen: Front, Heck, beide Seiten, Tacho, Kennzeichen und optionale Schäden.</p>
                        <a href={`/fotos/${token}`} target="_blank" rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-2 font-black text-white rounded-lg px-4 py-2.5 text-sm"
                            style={{ background: accent }} data-testid="leasing-photo-guide-start">
                            <Camera className="w-4 h-4" /> Geführte Fotoaufnahme starten
                        </a>
                    </div>
                    <div className="text-center">
                        <div className="bg-white p-2 rounded-lg border inline-block">
                            <QRCodeSVG value={`${typeof window !== "undefined" ? window.location.origin : ""}/fotos/${token}`} size={104} fgColor="#111827" />
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1 max-w-[120px] mx-auto">Mit dem Handy scannen &amp; loslegen</div>
                    </div>
                </div>
            </div>

            <div className="rounded-lg p-4 border-2 border-dashed" style={{ borderColor: accent }} data-testid="leasing-damage-upload-block">
                <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-black" style={{ color: accent }}>
                            <Upload className="w-4 h-4" /> Unterlagen
                        </div>
                        <h4 className="text-base font-black text-gray-900 mt-1">Weitere Unterlagen hochladen</h4>
                        <p className="text-sm text-gray-600 mt-1">Rechnungen, Servicenachweise, Gutachten oder zusätzliche Fotos – einfach als Bild oder PDF anhängen.</p>
                        <label className="mt-3 inline-flex items-center gap-2 font-black text-white rounded-lg px-4 py-2.5 text-sm cursor-pointer" style={{ background: accent }} data-testid="leasing-damage-upload-general">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Datei auswählen
                            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onUpload(e, "")} />
                        </label>
                    </div>
                </div>
                {uploads.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="leasing-damage-uploads">
                        {uploads.map((u) => (
                            <a key={u.id} href={`${BASE}${u.url}`} target="_blank" rel="noreferrer" className="text-xs border rounded-sm px-2 py-1 inline-flex items-center gap-1 bg-white">
                                <ImageIcon className="w-3 h-3" /> {u.filename || "Datei"}
                            </a>
                        ))}
                    </div>
                )}
            </div>

            {onMessageChange && (
                <div className="bg-white border rounded-lg p-4 shadow-sm" data-testid="leasing-damage-message-block">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2" style={{ color: accent }}>
                        <MessageSquare className="w-4 h-4" /> Nachricht an Ihren Händler <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea value={message || ""} onChange={(e) => onMessageChange(e.target.value)} rows={2} placeholder="z. B. Wunschtermin, Rückfragen oder Hinweise zum Fahrzeug…" className="w-full border rounded-md px-3 py-2 text-sm" data-testid="leasing-damage-message" />
                </div>
            )}

            <button onClick={compute} disabled={computing} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-sm py-3 text-sm inline-flex items-center justify-center gap-2" data-testid="leasing-damage-compute">
                {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Unverbindliche Ersteinschätzung berechnen
            </button>

            {estimate && (
                <div className="bg-white border-2 border-indigo-200 rounded-lg p-5 shadow-sm" data-testid="leasing-damage-result">
                    <div className="font-black text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-indigo-600" /> Voraussichtliche Rückgabekosten</div>
                    <div className="mt-3 space-y-1 text-sm">
                        {(estimate.lines || []).map((l, i) => (
                            <div key={i} className="flex justify-between border-b border-gray-50 py-1" data-testid={`leasing-damage-line-${i}`}>
                                <span className="text-gray-700">{l.label}</span><span className="font-semibold">{eur(l.amount)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-3 pt-2 border-t-2 font-black text-lg" data-testid="leasing-damage-total">
                        <span>Summe (Brutto, inkl. 19 % MwSt.)</span><span>{eur(estimate.total)}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-2">{cat.disclaimer}</div>
                </div>
            )}
        </div>
    );
}
